/**
 * REMOTE CONFIG — the client's copy of the wire shape.
 *
 * Hand-mirrored from `backend/src/game/config/config.types.ts`. It is a mirror
 * and not a shared package because `shared/` is the SIM's boundary: everything
 * in there is imported by the deterministic engine and must import nothing, and
 * a config document is neither deterministic nor something the sim may see.
 * `frontend/src/engine/config/config.test.ts` pins the constants and the codec
 * so a drift is a failing test rather than a silent desync.
 *
 * ── THE ONE SENTENCE FROM ADR-0004 ──────────────────────────────────────────
 * The client's bundled card table is a CACHE. The server's is authoritative.
 * On any version mismatch the client fetches the server's table BEFORE a match
 * starts — never during one, and never by guessing.
 *
 * ── WHAT THE CLIENT MAY DECIDE WITH THIS ────────────────────────────────────
 * Nothing of value (HARD LAW 2). A flag read here hides or shows a tab. The
 * server independently refuses the endpoint behind that tab. A card stat read
 * here draws a tooltip; the server's table is what the match runs on, and the
 * join gate refuses a client whose version disagrees rather than trusting it.
 */
import type { Card } from '@rga/sim';

/** Discriminator on the envelope. A document without it is not one of ours. */
export const CONFIG_DOC_KIND = 'rga.remote-config';

/** Envelope format this build understands. */
export const CONFIG_FORMAT = 1;

/**
 * The version the table BUNDLED WITH THIS BUILD claims.
 *
 * Must equal `BUNDLED_CARD_TABLE_VERSION` on the server. `0` is reserved for "I
 * have never fetched anything", so the bundled table starts at 1 and no
 * published revision may ever claim 0.
 */
export const BUNDLED_CARD_TABLE_VERSION = 1;

/** `localStorage` key for the cached document. Versioned, so a format bump is a miss. */
export const CONFIG_CACHE_KEY = 'rga.config.v1';

/** A flag as the PUBLIC document carries it — no descriptions, no account ids. */
export interface PublicFlag {
  readonly key: string;
  readonly enabled: boolean;
  readonly rollout: number;
}

export interface PublicConfigPayload {
  readonly format: number;
  readonly cardTableVersion: number;
  readonly cards: readonly Card[];
  readonly flags: readonly PublicFlag[];
}

export interface RemoteConfigEnvelope {
  readonly kind: string;
  readonly revision: number;
  readonly cardTableVersion: number;
  /** FNV-1a-32 of the canonical payload. The client recomputes and compares it. */
  readonly checksum: string;
  readonly cardsChecksum: string;
  readonly publishedAt: string;
  readonly note: string;
  readonly payload: PublicConfigPayload;
}

/** `GET /api/config/version` — the pre-match poll. */
export interface RemoteConfigVersionView {
  readonly revision: number;
  readonly cardTableVersion: number;
  readonly checksum: string;
  readonly cardsChecksum: string;
  readonly publishedAt: string;
}

/** `GET /api/config/flags` — the server's own answers for the signed-in account. */
export interface FlagAnswersView {
  readonly revision: number;
  readonly flags: Readonly<Record<string, boolean>>;
}

/**
 * The result of the pre-match freshness gate.
 *
 *   `fresh`      the cached version already matches the server's.
 *   `refreshed`  a newer document was fetched, validated and adopted.
 *   `rejected`   the server's document did not validate. The cached one is
 *                KEPT and the caller must not start a match: joining would be
 *                refused at the gate anyway, and saying so here is the only
 *                chance to say it in words.
 *   `offline`    the check could not be made. The caller decides; the join gate
 *                is still there and still refuses a genuine mismatch.
 */
export type ConfigFreshness = 'fresh' | 'refreshed' | 'rejected' | 'offline';

export interface FreshnessResult {
  readonly status: ConfigFreshness;
  /** The version the client should now claim on a join. */
  readonly cardTableVersion: number;
  readonly revision: number;
  /** Present for `rejected`: why the document was refused. */
  readonly reason: string | null;
}

/** What the store holds. `null` cards means "the bundled table is in use". */
export interface ConfigState {
  readonly revision: number;
  readonly cardTableVersion: number;
  readonly checksum: string;
  readonly cardsChecksum: string;
  /** The fetched roster, or `null` while the bundled one is current. */
  readonly cards: readonly Card[] | null;
  readonly flags: readonly PublicFlag[];
  /** Server-evaluated answers for this account; `null` until fetched. */
  readonly answers: Readonly<Record<string, boolean>> | null;
  /** Has a document from the server been adopted in this session? */
  readonly loaded: boolean;
  readonly publishedAt: string;
}
